import React from "react"
import { graphql } from "gatsby"

import CmsPage from "@utils/cmsPage"
import { getPrototypePageSlice } from "@utils/prototypeSlices"

const prototypeSlice = getPrototypePageSlice("compare")

const compareButtonRadiusPatch = `
  .finder-sec .pill,
  .finder-sec .seg-btn,
  .finder-sec .vp-ctas a,
  .final-cta .btn {
    border-radius: 8px;
  }

  .cms-page .final-cta {
    background: #fff;
    color: var(--ink, #0f172a);
  }

  .cms-page .final-cta h2 {
    color: var(--ink, #0f172a);
  }

  .cms-page .final-cta p,
  .cms-page .compare-final-cta .compare-final-cta__summary,
  .cms-page .compare-final-cta .compare-final-cta__summary * {
    color: var(--ink-2, #475569) !important;
  }

  .hero-jump a,
  .finder-sec .vp-ctas a,
  .switchers-foot a {
    align-items: center;
    display: inline-flex;
    justify-content: center;
    min-height: 44px;
  }

  .finder-sec .seg {
    border-radius: 10px;
  }

  .finder-sec .finder-step-hint {
    color: #334155;
    font-size: 15px;
    line-height: 1.55;
  }

  .finder-sec .vp-stripe-note {
    background: rgba(255,255,255,.04);
    border: 1px solid rgba(255,255,255,.08);
    border-radius: 12px;
    color: rgba(255,255,255,.78);
    font-size: 12px;
    line-height: 1.5;
    margin-top: 16px;
    padding: 14px 14px 12px;
  }

  .finder-sec .vp-stripe-note-row {
    align-items: flex-start;
    display: flex;
    gap: 10px;
  }

  .finder-sec .vp-stripe-note b {
    color: #fff;
    font-weight: 600;
  }

  .finder-sec .vp-stripe-mark {
    align-items: center;
    background: rgba(110,231,216,.18);
    border-radius: 50%;
    color: #6ee7d8;
    display: inline-flex;
    flex-shrink: 0;
    font-family: var(--displayFamily);
    font-size: 13px;
    font-weight: 700;
    height: 22px;
    justify-content: center;
    width: 22px;
  }

  .finder-sec .vp-stripe-disclaimer {
    background: rgba(251,191,36,.1);
    border: 1px solid rgba(251,191,36,.25);
    border-radius: 8px;
    color: #fde68a;
    font-size: 11.5px;
    line-height: 1.45;
    margin-top: 10px;
    padding: 9px 11px;
  }

  .finder-sec .vp-stripe-disclaimer b {
    color: #fff;
    font-weight: 600;
  }

  .finder-sec .pill,
  .finder-sec .pill.on,
  .finder-sec .pill:active,
  .finder-sec .pill:focus,
  .finder-sec .pill:focus-visible {
    background-clip: padding-box;
    box-shadow: none !important;
  }
`

const compareMobileStyles = `
@media (max-width: 960px) {
  .cms-page .hero,
  .cms-page .hero-inner,
  .cms-page .finder-head,
  .cms-page .finder-card,
  .cms-page .verdict-panel,
  .cms-page .arena-head,
  .cms-page .switchers-head,
  .cms-page .testim-sec,
  .cms-page .final-cta,
  .cms-page .mc-section__head,
  .cms-page .cms-page__section-head {
    text-align: center;
  }

  .cms-page .hero-inner,
  .cms-page .finder-head,
  .cms-page .arena-head,
  .cms-page .switchers-head,
  .cms-page .final-cta {
    justify-items: center;
  }

  .cms-page .eyebrow,
  .cms-page .kick,
  .cms-page .tag,
  .cms-page .finder-eyebrow,
  .cms-page .arena-eyebrow,
  .cms-page .testim-ctx,
  .cms-page .mc-section__eyebrow,
  .cms-page .cms-page__eyebrow {
    display: inline-flex;
    justify-content: center;
    margin-left: auto;
    margin-right: auto;
    text-align: center;
  }

  .cms-page .hero h1,
  .cms-page .finder-head h2,
  .cms-page .finder-step-title,
  .cms-page .arena-head h2,
  .cms-page .switchers-head h3,
  .cms-page .testim-sec blockquote,
  .cms-page .final-cta h2,
  .cms-page .mc-section__heading,
  .cms-page .cms-page__section-title {
    text-align: center;
  }

  .cms-page .lede,
  .cms-page .hero .lede,
  .cms-page .finder-head p,
  .cms-page .arena-head p,
  .cms-page .switchers-head p,
  .cms-page .final-cta p,
  .cms-page .mc-section__summary,
  .cms-page .cms-page__section-copy {
    margin-left: auto;
    margin-right: auto;
    text-align: center;
  }

  .cms-page .hero-jump,
  .cms-page .vp-ctas,
  .cms-page .final-cta div,
  .cms-page .mc-section__actions {
    align-items: stretch;
    flex-direction: column;
    width: 100%;
  }

  .cms-page .hero-jump a,
  .cms-page .vp-ctas a,
  .cms-page .final-cta .btn,
  .cms-page .mc-section__actions .btn {
    width: 100%;
  }

  .cms-page .finder-sec .pill-row {
    justify-content: center;
  }

  .cms-page .finder-sec .seg {
    display: flex;
    width: 100%;
  }

  .cms-page .finder-sec .seg-btn {
    flex: 1 1 0;
    justify-content: center;
    text-align: center;
  }

  .cms-page .finder-sec .vp-stripe-note {
    text-align: left;
  }
}
`

const ComparePage = ({ data }) => {
  const page = data.allDatoCmsMcV2PageCompare.nodes[0] || {}
  const sectionCollection = page.sectionCollection || {}
  const heroSlot = (
    <section className="hero">
      <div className="hero-inner">
        <h1>
          Answer 4 questions.
          <br />
          <em>We'll tell you who fits.</em>
        </h1>
        <p className="lede">
          Then drop into head-to-head mode for the two tools you're seriously considering.
          Cost curves, category-by-category scorecard, switcher quotes.
        </p>
        <div className="hero-jump">
          <span>Already shortlisted?</span>
          <a href="#arena">Jump to the shootout ↓</a>
        </div>
      </div>
    </section>
  )

  return (
    <CmsPage
      slug={page.slug}
      seo={page.seo}
      eyebrow={page.eyebrow}
      heading={page.heading}
      summary={page.summary}
      body={page.body}
      bodyHeading={page.bodyHeading || "Comparison Overview"}
      heroMediaType={page.heroMediaType}
      heroImageUrl={page.heroImageUrl}
      heroImageAlt={page.heroImageAlt}
      heroVideoUrl={page.heroVideoUrl}
      heroVideoPosterUrl={page.heroVideoPosterUrl}
      heroSlot={heroSlot}
      contentSections={sectionCollection.contentSections || []}
      contentHtml={page.contentHtml}
      fallbackContentHtml={prototypeSlice.contentHtml}
      extraStyles={`${prototypeSlice.extraStyles || ""}\n${compareButtonRadiusPatch}\n${compareMobileStyles}`}
      extraHeadLinks={prototypeSlice.extraHeadLinks}
      emptyMessage="Create or seed the compare record in mc_v2_page_compare."
    />
  )
}

export default ComparePage

export const query = graphql`
  query CompareCmsPageQuery {
    allDatoCmsMcV2PageCompare(filter: { slug: { eq: "compare" } }, limit: 1) {
      nodes {
        seo {
          ...CmsSeoFields
        }
        slug
        eyebrow
        heading
        summary
        body
        # bodyHeading  # TODO(T-014 add the Dato field, T-015 then uncomment): blocked on DatoCMS schema
        heroMediaType
        heroImageUrl
        heroImageAlt
        heroVideoUrl
        heroVideoPosterUrl
        contentHtml
        sectionCollection {
          contentSections {
            ...CmsHtmlSectionFields
            ... on DatoCmsMcV2HtmlSection {
              compareCollection {
                compareCategoryEntries {
                  categoryKey
                  title
                  note
                  scoreMoonclerk
                  scorePayfunnel
                  scoreStripe
                  scoreChargebee
                  scoreRecurly
                  scoreLemon
                  scorePaddle
                }
                compareQuoteEntries {
                  toolKey
                  context
                  quoteHtml
                  attributionHtml
                }
              }
            }
          }
        }
      }
    }
  }
`
